import { __ } from '@wordpress/i18n';
import { useSelect, dispatch } from '@wordpress/data';
import { useState, useEffect } from '@wordpress/element';

import {
	BlockControls,
	InspectorControls,
	InnerBlocks,
	useBlockProps,
	useInnerBlocksProps,
	__experimentalUseBorderProps as useBorderProps,
	__experimentalGetSpacingClassesAndStyles as useSpacingProps,
	store as blockEditorStore,
} from '@wordpress/block-editor';
import {
	store as blocksStore,
	createBlocksFromInnerBlocksTemplate,
} from '@wordpress/blocks';
import { useEntityProp } from '@wordpress/core-data';
import { ToolbarGroup, ToolbarButton } from '@wordpress/components';
import { store as noticesStore } from '@wordpress/notices';

import BlockVariationPicker from './placeholder';
import Appearance from './settings/appearance';
import OpenTriggers from './settings/open-trigger';
import CloseTriggers from './settings/close-trigger';
import Behaviors from './settings/behaviors';
import { RulesModal } from '../../modal-conditions/modal';
import { TemplatesModal } from './library';
import './editor.scss';
import Controls from './settings/controls';

import classnames from 'classnames';
import { layout } from '@wordpress/icons';
import { useAfterSave } from './use-after-save';

function Edit( props ) {
	const { attributes, setAttributes, clientId, name } = props;

	const {
		backgroundColor,
		boxShadow,
		overlayColor,
		align,
		borderRadius,
		id,
		uuid,
		disableScroll,
		animation,
	} = attributes;

	const { postId, deviceType, postType } = useSelect( ( select ) => {
		return {
			deviceType: select( 'core/editor' ).getDeviceType(),
			postId: select( 'core/editor' ).getCurrentPostId(),
			postType: select( 'core/editor' ).getCurrentPostType(),
		};
	}, [] );

	const [ meta ] = useEntityProp( 'postType', postType, 'meta', postId );
	const { createNotice } = dispatch( noticesStore );

	const { hasInnerBlocks, innerBlocks, isPreview } = useSelect(
		( select ) => {
			const {
				getClientIdsOfDescendants,
				getBlockNamesByClientId,
				getBlock,
				getSettings,
			} = select( blockEditorStore );
			const block = getBlock( clientId );
			const blocks = getClientIdsOfDescendants( clientId );
			return {
				hasInnerBlocks: !! ( block && block.innerBlocks.length ),
				innerBlocks: getBlockNamesByClientId( blocks ),
				isPreview: getSettings().isPreviewMode,
			};
		},
		[ clientId ]
	);

	const isAfterSave = useAfterSave();

	useEffect( () => {
		if ( isPreview ) {
			return;
		}
		if ( ! isPreview && Number( uuid ) !== postId ) {
			setAttributes( { uuid: postId, id: `popper-${ postId }` } );
		}
	}, [] );

	useEffect( () => {
		if ( isAfterSave ) {
			// Add your code here that must run after the post is saved.
			if (
				id &&
				meta.popper_rules.running &&
				( ! meta.popper_rules.location.length ||
					! meta.popper_rules.device.length )
			) {
				createNotice(
					'warning',
					__(
						'This popup will not be shown unless you add a display rule.',
						'popper'
					),
					{
						id: 'popper-notice',
						isDismissible: true,
						actions: [
							{
								label: __( 'Display rules', 'popper' ),
								onClick: () => setModalOpen( 'options' ),
								variant: 'primary',
								className: 'is-small',
							},
						],
					}
				);
			} else {
				dispatch( 'core/notices' ).removeNotice( 'popper-notice' );
			}
		}
	}, [ isAfterSave ] );

	const [ isModalOpen, setModalOpen ] = useState( false );

	const { selectBlock } = dispatch( blockEditorStore );

	const borderProps = useBorderProps( attributes );
	const spacingProps = useSpacingProps( attributes );

	const selectPopper = ( e ) => {
		if ( e.target.id === 'popper-backdrop' ) {
			selectBlock( clientId );
		}
	};

	const modalStyle = {
		borderRadius,
		...borderProps.style,
		...spacingProps.style,
		'--popper-animation': attributes.animation,
	};

	const overlayStyle = {
		backgroundColor: overlayColor,
	};

	if (
		( overlayColor && 'center center' !== align ) ||
		! attributes.className?.includes( 'is-style-default' )
	) {
		overlayStyle.backgroundColor = undefined;
	}

	if ( backgroundColor ) {
		modalStyle.backgroundColor = backgroundColor;
	}

	const popperClass = classnames(
		'wp-block-popper',
		'wp-block-formello-popper',
		'alignfull',
		{
			'no-scroll': disableScroll,
			'is-empty': ! innerBlocks.includes( 'popper/body' ),
			'wp-block-popper__animate': !! animation,
		},
		boxShadow,
		borderRadius,
		borderProps.className
	);

	const backdropClass = classnames( 'wp-block-popper-is-open alignfull', {
		'wp-block-formello-popper--nobg':
			align !== 'center center' ||
			undefined === overlayColor ||
			! attributes.className?.includes( 'is-style-default' ),
	} );

	if ( 'Mobile' === deviceType ) {
		modalStyle.width = undefined;
	}

	const blockProps = useBlockProps( {
		className: popperClass,
		style: modalStyle,
	} );

	const { children, ...innerBlocksProps } = useInnerBlocksProps( blockProps, {
		template: [ [ 'popper/body' ] ],
		templateLock: false,
		renderAppender: hasInnerBlocks
			? undefined
			: InnerBlocks.DefaultBlockAppender,
	} );

	return (
		<>
			<InspectorControls>
				<OpenTriggers { ...props } />
				<CloseTriggers { ...props } />
				<Behaviors { ...props } />
			</InspectorControls>
			<Appearance { ...props } />
			<BlockControls>
				<Controls
					attributes={ attributes }
					setAttributes={ setAttributes }
				/>
				<ToolbarGroup>
					<ToolbarButton
						label={ __( 'Template', 'popper' ) }
						icon={ layout }
						onClick={ () => {
							setModalOpen( 'templates' );
						} }
					/>
				</ToolbarGroup>
				<ToolbarGroup>
					<ToolbarButton
						label={ __( 'Display Rules', 'popper' ) }
						icon={ 'visibility' }
						onClick={ () => {
							setModalOpen( 'options' );
						} }
					/>
				</ToolbarGroup>
			</BlockControls>

			<div
				className={ backdropClass }
				onClick={ selectPopper }
				onKeyDown={ selectPopper }
				style={ overlayStyle }
				id="popper-backdrop"
				role="button"
				tabIndex="-1"
			>
				<dialog
					open
					data-align={ align }
					className={ popperClass }
					{ ...innerBlocksProps }
				>
					{ children }
				</dialog>
			</div>

			{ 'templates' === isModalOpen && (
				<TemplatesModal
					clientId={ clientId }
					onRequestClose={ () => setModalOpen( false ) }
					blockName={ name }
				/>
			) }
			{ 'options' === isModalOpen && (
				<RulesModal
					onRequestClose={ () => setModalOpen( false ) }
					{ ...props }
				/>
			) }
		</>
	);
}

function Placeholder( props ) {
	const { name, clientId, setAttributes } = props;

	const { replaceInnerBlocks } = dispatch( 'core/block-editor' );

	const defaultVariation = useSelect(
		( select ) => {
			const { getDefaultBlockVariation } = select( blocksStore );
			return typeof getDefaultBlockVariation === 'undefined'
				? null
				: getDefaultBlockVariation( name );
		},
		[ name ]
	);

	const variations = useSelect(
		( select ) => {
			const { getBlockVariations } = select( blocksStore );
			return getBlockVariations( name, 'block' );
		},
		[ name ]
	);

	return (
		<div
			{ ...useBlockProps( {
				className: 'popper-placeholder',
			} ) }
		>
			<BlockVariationPicker
				icon={ 'external' }
				label={ 'Popup' }
				instructions={ __(
					'Select a template to start with.',
					'popper'
				) }
				variations={ variations }
				clientId={ clientId }
				setAttributes={ setAttributes }
				allowSkip
				onSelect={ ( nextVariation = defaultVariation ) => {
					if ( nextVariation.attributes ) {
						setAttributes( nextVariation.attributes );
					}
					if ( nextVariation.innerBlocks ) {
						replaceInnerBlocks(
							props.clientId,
							createBlocksFromInnerBlocksTemplate(
								nextVariation.innerBlocks
							),
							true
						);
					}
				} }
			/>
		</div>
	);
}

const PopperEdit = ( props ) => {
	const { clientId } = props;
	const hasInnerBlocks = useSelect(
		( select ) => {
			const { getBlock } = select( 'core/block-editor' );
			const block = getBlock( clientId );
			return !! ( block && block.innerBlocks.length );
		},
		[ clientId ]
	);
	const Component = hasInnerBlocks ? Edit : Placeholder;

	return <Component { ...props } />;
};

export default PopperEdit;
